import { BRAND_CONFIG } from '@@brand'
import { useTranslation } from 'react-i18next'

import { useFetchCustomLoyaltyLevelInfo } from '@entities/Loyalty'

import { useAuth } from '@features/auth'

import styles from './LoyaltyProgress.module.scss'

const COUPON_LEVELS = [
  { purchases: 5, amount: 50 },
  { purchases: 10, amount: 60 },
  { purchases: 15, amount: 70 },
  { purchases: 20, amount: 80 },
  { purchases: 25, amount: 100 },
]

export default function LoyaltyProgress () {
  const { t } = useTranslation('loyalty')
  const { isAuthorized, requireRegistration } = useAuth()
  
  
  const { data, loading } = useFetchCustomLoyaltyLevelInfo({
    code: 'StatusSystem3',
    skip: !isAuthorized || requireRegistration
  })

  const currentPurchases = data?.currentValue ?? 0

  const currentIndex = COUPON_LEVELS.findIndex(
    l => l.purchases > currentPurchases
  )

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.root}>
          <div className={styles.skeleton} />
        </div>
      </div>
    )
  }

  if (!isAuthorized) {
    return null
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.root}>
          <div className={styles.skeleton} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.root}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('loyalty-progress.title')}</h3>
        </div>

        <div className={styles.levels}>
          {COUPON_LEVELS.map((item, index) => {
            const isActive = index <= currentIndex
            
            // Вычисляем процент заполнения
            let fillPercentage = 0
            if (index === 0) {
              fillPercentage = Math.min((currentPurchases / item.purchases) * 100, 100)
            }
            else if (index === currentIndex) {
              const prevLevel = COUPON_LEVELS[index - 1]
              const range = item.purchases - prevLevel.purchases
              const progress = currentPurchases - prevLevel.purchases
              fillPercentage = Math.min((progress / range) * 100, 100)
            }
            else if (index < currentIndex) {
              fillPercentage = 100
            }

            if (currentPurchases > 0 && fillPercentage === 0 && index === currentIndex) {
              fillPercentage = 5
            }

            const circumference = 2 * Math.PI * 30
            const dashLength = (circumference * fillPercentage) / 100

            return (
              <div key={item.purchases} className={styles.levelItem}>
                <svg height="68" width="68" viewBox="0 0 68 68" className={styles.circle}>
                  {/* Фоновый круг */}
                  <circle
                    cx="34"
                    cy="34"
                    r="30"
                    className={styles.circleBackground}
                  />
                  {/* Заполненный круг */}
                  {(fillPercentage > 0 || (currentPurchases > 0 && index <= currentIndex)) && (
                    <circle
                      cx="34"
                      cy="34"
                      r="30"
                      className={`${styles.circleProgress} ${isActive ? styles.active : ''}`}
                      style={{
                        strokeDasharray: `${dashLength} ${circumference}`,
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center'
                      }}
                    />
                  )}
                  <text
                    x="34"
                    y="39"
                    textAnchor="middle"
                    className={`${styles.circleText} ${isActive ? styles.active : ''}`}
                  >
                    {item.purchases}
                  </text>
                </svg>

                <div className={`${styles.badge} ${isActive ? styles.active : ''}`}>
                  {item.amount}{BRAND_CONFIG.currency.symbol}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Информационный блок справа на больших экранах */}
      <div className={styles.infoBlock}>
        <h4 className={styles.infoBlock__title}>{t('loyalty-progress.info.title')}</h4>
        <p className={styles.infoBlock__text}>
          {t('loyalty-progress.info.description1')}
        </p>
        <p className={styles.infoBlock__text}>
          {t('loyalty-progress.info.description2')}
        </p>
      </div>
    </div>
  )
}
